// Visitor-scoped Supabase client used by the public chat widget.
// Sends the visitor's identifier as an `x-visitor-id` header so RLS policies
// can scope SELECT/INSERT/UPDATE on chat_conversations and chat_messages
// to the row(s) belonging to that specific visitor.
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

let cached: { visitorId: string; client: SupabaseClient<Database> } | null = null;

export function getVisitorSupabase(visitorId: string): SupabaseClient<Database> {
  if (!visitorId) {
    throw new Error('visitorId is required to create a visitor-scoped Supabase client');
  }
  if (cached && cached.visitorId === visitorId) return cached.client;

  const client = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      // Use a separate storage key so we don't collide with the main client session
      storageKey: `pacers-visitor-${visitorId}`,
      persistSession: false,
      autoRefreshToken: false,
    },
    global: {
      headers: {
        'x-visitor-id': visitorId,
      },
    },
    realtime: {
      params: {
        // Forward visitor id to realtime for RLS (PostgREST-style header surfaced to RLS)
        apikey: SUPABASE_PUBLISHABLE_KEY,
      },
    },
  });

  cached = { visitorId, client };
  return client;
}
